import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, ButtonGroup, Divider, Grid, Menu, MenuItem, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import useInfoDialog from 'common/hooks/useInfoDialog';
import { updateCatalogDrug } from 'store/thunks/catalogThunks';

import CatalogAvailabilitySection from './CatalogAvailabilitySection';
import EditCatalogDialog from './EditCatalogDialog';
import { formatDrugName, formatForm, formatPackSize, formatStrength } from '../utils/format';

const DetailField = ({ label, children }) => (
  <Grid item xs={4}>
    <Grid container direction='column'>
      <Typography variant='body2' color='primary'>
        {label}
      </Typography>
      <Typography variant='body1'>{children}</Typography>
    </Grid>
  </Grid>
);

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

DetailField.defaultProps = {
  children: '-',
};

const CatalogDetails = ({ item }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const [editOpen, setEditOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  if (!item) {
    return (
      <Box sx={{ mt: 3, px: 2 }}>
        <Typography variant='h5' component='h4' color='primary' sx={{ mb: 3 }}>
          Catalog Details
        </Typography>

        <Typography variant='body1' component='span'>
          Select an active catalog to start
        </Typography>
      </Box>
    );
  }

  const drugName = formatDrugName(item.drugName);

  const handleToggleStatus = () => {
    setMenuAnchor(null);

    showInfoDialog({
      title: `Update ${drugName} Catalog Status`,
      message: `Please confirm to ${item.isActive ? 'deactivate' : 'reactivate'} ${drugName}?`,
      callback: () =>
        dispatch(updateCatalogDrug({ catalogId: item.catalogId, isActive: !item.isActive })).then(
          (response) => {
            if (response) {
              enqueueSnackbar('Catalog updated successfully.', { variant: 'success' });
            }
          }
        ),
      isWarning: true,
      cancelable: true,
      confirmButtonText: 'Confirm',
    });
  };

  return (
    <Box sx={{ mt: 3, px: 2 }}>
      <InfoDialog />
      <EditCatalogDialog open={editOpen} item={item} onClose={() => setEditOpen(false)} />

      <Grid container justifyContent='space-between' alignItems='center'>
        <Typography variant='h5' component='h2' color='primary'>
          Catalog Details - {drugName}
        </Typography>

        <ButtonGroup variant='outlined' sx={{ borderRadius: 1 }}>
          <Button sx={{ borderRadius: 1 }} size='large' onClick={() => setEditOpen(true)}>
            Edit
          </Button>
          <Button
            sx={{ borderRadius: 1 }}
            size='small'
            aria-label={`more on ${item.catalogId}`}
            id='more-button'
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            <MoreVertIcon />
          </Button>
        </ButtonGroup>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          slotProps={{ list: { 'aria-labelledby': 'more-button' } }}
        >
          <MenuItem onClick={handleToggleStatus}>
            {item.isActive ? 'Deactivate' : 'Activate'}
          </MenuItem>
        </Menu>
      </Grid>

      <Grid container direction='column' sx={{ p: 2 }}>
        <Grid container sx={{ mb: 3 }}>
          <DetailField label='Drug Name'>{drugName}</DetailField>
          <DetailField label='Drug Strength'>{formatStrength(item.strength)}</DetailField>
          <DetailField label='Catalog Id'>{item.catalogId}</DetailField>
        </Grid>

        <Grid container sx={{ mb: 3 }}>
          <DetailField label='Product Id'>{item.productId || '-'}</DetailField>

          <Grid item xs={4}>
            <Grid container direction='column'>
              <Typography variant='body2' color='primary'>
                Compounding Drug
              </Typography>
              {item.isCompoundingEligible ? (
                <CheckIcon color='success' />
              ) : (
                <CloseIcon color='error' />
              )}
            </Grid>
          </Grid>

          {/* Sig associations for this drug live in Sig Management (a separate catalog-to-sig
              mapping), not on the listCatalog response this screen is built on -- so this is a
              static placeholder rather than a wired-up field. */}
          <DetailField label='Sigs'>No sigs for this drug</DetailField>
        </Grid>

        <Grid container>
          <DetailField label='Drug Code'>{item.drugCode || '-'}</DetailField>
          <DetailField label='Pack Size'>
            {formatPackSize(item.packSizeValue, item.packSizeUnit)}
          </DetailField>
          <DetailField label='Form'>{formatForm(item.formAlias, item.dosageForm)}</DetailField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <CatalogAvailabilitySection item={item} drugName={drugName} />
    </Box>
  );
};

CatalogDetails.propTypes = {
  item: PropTypes.shape({
    catalogId: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    productId: PropTypes.string,
    drugCode: PropTypes.string,
    formAlias: PropTypes.string,
    dosageForm: PropTypes.string,
    packSizeValue: PropTypes.string,
    packSizeUnit: PropTypes.string,
    isCompoundingEligible: PropTypes.bool,
    isActive: PropTypes.bool,
    isAvailable: PropTypes.bool,
    isPartnerActive: PropTypes.bool,
    whitelisted: PropTypes.arrayOf(PropTypes.string),
  }),
};

CatalogDetails.defaultProps = {
  item: null,
};

export default CatalogDetails;
